import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Lock, TrendingUp, BarChart3, Users, Mail, Tag, ShoppingCart, Printer, Download, Package, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sub, icon }) {
  return (
    <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)]">{label}</span>
        {icon}
      </div>
      <p className="font-heading text-3xl text-[var(--color-text-dark)]">{value}</p>
      {sub && <p className="font-body text-xs text-[var(--color-text-mid)] mt-1">{sub}</p>}
    </div>
  );
}

function BarRow({ label, count, max }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="font-body text-xs text-[var(--color-text-mid)] w-24 shrink-0 truncate capitalize">{label}</span>
      <div className="flex-1 h-5 rounded bg-[var(--color-cream)] overflow-hidden">
        <div className="h-full rounded" style={{ width: `${pct}%`, backgroundColor: "var(--color-gold)" }} />
      </div>
      <span className="font-accent text-xs text-[var(--color-text-dark)] w-8 text-right">{count}</span>
    </div>
  );
}

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token"));
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [printOrders, setPrintOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  // Discount form
  const [newCode, setNewCode] = useState("");
  const [newPercent, setNewPercent] = useState(10);
  const [newMaxUses, setNewMaxUses] = useState("");

  const headers = { "X-Admin-Token": token || "" };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const resp = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_token", resp.data.token);
      setToken(resp.data.token);
    } catch {
      toast.error("Invalid password");
    }
  };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [s, o, d, p] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/discount-codes`, { headers }),
        axios.get(`${API}/admin/print-orders`, { headers }),
      ]);
      setStats(s.data);
      setOrders(o.data.orders || []);
      setDiscounts(d.data.codes || []);
      setPrintOrders(p.data.orders || []);
    } catch (err) {
      if (err.response?.status === 401) { setToken(null); sessionStorage.removeItem("admin_token"); }
      else toast.error("Failed to load data — try logging out and back in");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createDiscount = async (e) => {
    e.preventDefault();
    if (!newCode.trim() || newPercent < 1 || newPercent > 100) { toast.error("Invalid code or percent"); return; }
    try {
      await axios.post(`${API}/admin/discount-codes`, {
        code: newCode, discount_percent: newPercent,
        max_uses: newMaxUses ? parseInt(newMaxUses) : null,
      }, { headers });
      toast.success(`Code ${newCode.toUpperCase()} created`);
      setNewCode(""); setNewPercent(10); setNewMaxUses("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleDiscount = async (id) => {
    try {
      await axios.post(`${API}/admin/discount-codes/${id}/toggle`, {}, { headers });
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const toggleFulfill = async (orderId, currentlyFulfilled) => {
    try {
      const action = currentlyFulfilled ? "unfulfill" : "fulfill";
      await axios.post(`${API}/admin/print-orders/${orderId}/${action}`, {}, { headers });
      fetchAll();
    } catch { toast.error("Failed to update"); }
  };

  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

  // Login screen
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-navy)]" data-testid="admin-login">
        <form onSubmit={handleLogin} className="w-full max-w-xs px-4">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Lock size={20} style={{ color: "var(--color-gold)" }} />
            <span className="font-heading text-2xl tracking-wider text-white">ADMIN</span>
          </div>
          <Input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="rounded-[8px] mb-4 bg-white/10 border-white/20 text-white placeholder:text-white/40"
            data-testid="admin-password-input"
          />
          <button type="submit" className="w-full font-heading tracking-widest uppercase py-3 rounded-[8px]" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }} data-testid="admin-login-btn">
            Access Dashboard
          </button>
        </form>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-cream)]">
        <div className="w-10 h-10 border-3 border-[var(--color-gold)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sportsEntries = stats ? Object.entries(stats.sports).sort((a, b) => b[1] - a[1]) : [];
  const breedEntries = stats ? Object.entries(stats.breeds).sort((a, b) => b[1] - a[1]) : [];
  const maxSport = sportsEntries.length > 0 ? sportsEntries[0][1] : 1;
  const maxBreed = breedEntries.length > 0 ? breedEntries[0][1] : 1;

  return (
    <div className="min-h-screen bg-[var(--color-cream)] pb-16" data-testid="admin-dashboard">
      {/* Header */}
      <div style={{ backgroundColor: "var(--color-navy)" }} className="px-4 md:px-8 py-4 flex items-center justify-between">
        <span className="font-heading text-xl tracking-wider text-white">CARD MY <span style={{ color: "var(--color-gold)" }}>DOG</span> <span className="text-white/40 text-sm ml-2">Admin</span></span>
        <button onClick={() => { setToken(null); sessionStorage.removeItem("admin_token"); }} className="font-body text-xs text-white/50 hover:text-white">Logout</button>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-8">
        {/* Admin Account */}
        {stats?.admin_account && (
          <div className="rounded-[12px] border border-[var(--color-gold)] p-4 mb-8 flex items-center gap-4" style={{ backgroundColor: "rgba(245,166,35,0.06)" }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--color-navy)" }}>
              <Lock size={16} style={{ color: "var(--color-gold)" }} />
            </div>
            <div>
              <p className="font-body text-sm font-medium text-[var(--color-text-dark)]">Admin Account: <span className="font-accent text-xs">{stats.admin_account.email}</span></p>
              <p className="font-body text-xs text-[var(--color-text-mid)]">{stats.admin_account.privileges}</p>
            </div>
          </div>
        )}

        {/* Print Orders to Fulfill */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl uppercase tracking-tight text-[var(--color-text-dark)] mb-4">
            <Package size={18} className="inline mr-2" style={{ color: "var(--color-gold)" }} />Print Orders to Fulfill
            {printOrders.filter(o => !o.print_fulfilled).length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full font-accent text-xs text-white" style={{ backgroundColor: "var(--color-error)" }}>
                {printOrders.filter(o => !o.print_fulfilled).length} pending
              </span>
            )}
          </h2>
          {printOrders.length === 0 ? (
            <p className="font-body text-sm text-[var(--color-text-light)]">No print orders yet.</p>
          ) : (
            <div className="space-y-4">
              {printOrders.map(po => (
                <div key={po.id} className={`bg-[var(--color-white)] rounded-[12px] border p-5 ${po.print_fulfilled ? 'border-[var(--color-border)] opacity-70' : 'border-[var(--color-gold)]'}`}>
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Card thumbnail */}
                    {po.full_image_url && (
                      <div className="shrink-0">
                        <img src={po.full_image_url} alt={po.dog_name} className="w-20 h-28 object-cover rounded-[8px] border border-[var(--color-border)]" />
                      </div>
                    )}
                    {/* Order details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-heading text-lg uppercase text-[var(--color-text-dark)]">{po.dog_name}</span>
                        <span className="font-accent text-xs text-[var(--color-text-mid)] capitalize">{po.sport} &middot; {po.position}</span>
                        {po.print_fulfilled ? (
                          <span className="font-accent text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600">Fulfilled</span>
                        ) : (
                          <span className="font-accent text-[10px] px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-600">Pending</span>
                        )}
                      </div>
                      <p className="font-body text-xs text-[var(--color-text-mid)] mb-1">
                        <span className="font-medium">{po.email}</span> &middot; #{po.id.slice(0, 8)} &middot; {fmt(po.amount_total)} &middot; {new Date(po.created_at).toLocaleDateString()}
                      </p>
                      {/* Shipping address */}
                      <div className="bg-[var(--color-cream)] rounded-[8px] p-3 mt-2">
                        <p className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] mb-1">Ship To</p>
                        <p className="font-body text-sm text-[var(--color-text-dark)] font-medium">{po.shipping_name}</p>
                        <p className="font-body text-xs text-[var(--color-text-mid)]">
                          {po.shipping_address_line1}{po.shipping_address_line2 ? `, ${po.shipping_address_line2}` : ''}<br />
                          {po.shipping_city}, {po.shipping_state} {po.shipping_zip}<br />
                          {po.shipping_country}
                        </p>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex flex-col gap-2 shrink-0">
                      <a
                        href={po.full_image_url}
                        download={`${po.dog_name.replace(/\s+/g, '-').toLowerCase()}-hd.png`}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-[8px] font-heading tracking-widest uppercase text-xs"
                        style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
                        data-testid={`print-download-${po.id}`}
                      >
                        <Download size={14} /> HD Image
                      </a>
                      <a
                        href={`${API}/download/${po.download_token}?format=original`}
                        download
                        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-[8px] font-heading tracking-widest uppercase text-xs border border-[var(--color-border)] text-[var(--color-text-mid)] hover:border-[var(--color-gold)]"
                      >
                        <Printer size={14} /> Print Version
                      </a>
                      <button
                        onClick={() => toggleFulfill(po.id, po.print_fulfilled)}
                        className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded-[8px] font-heading tracking-widest uppercase text-xs ${po.print_fulfilled ? 'border border-[var(--color-border)] text-[var(--color-text-mid)]' : 'text-white'}`}
                        style={po.print_fulfilled ? {} : { backgroundColor: "var(--color-success)" }}
                      >
                        <Check size={14} /> {po.print_fulfilled ? 'Undo Fulfill' : 'Mark Fulfilled'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Revenue Row */}
        <h2 className="font-heading text-2xl uppercase tracking-tight text-[var(--color-text-dark)] mb-4">Revenue</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Today" value={fmt(stats?.revenue?.today || 0)} icon={<TrendingUp size={16} style={{ color: "var(--color-gold)" }} />} />
          <StatCard label="This Week" value={fmt(stats?.revenue?.week || 0)} icon={<TrendingUp size={16} style={{ color: "var(--color-gold)" }} />} />
          <StatCard label="This Month" value={fmt(stats?.revenue?.month || 0)} icon={<TrendingUp size={16} style={{ color: "var(--color-gold)" }} />} />
          <StatCard label="All Time" value={fmt(stats?.revenue?.all_time || 0)} icon={<TrendingUp size={16} style={{ color: "var(--color-gold)" }} />} />
        </div>

        {/* Key Metrics Row */}
        <h2 className="font-heading text-2xl uppercase tracking-tight text-[var(--color-text-dark)] mb-4">Key Metrics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Cards Generated" value={stats?.cards?.generated || 0} sub={`${stats?.cards?.purchased || 0} purchased (${stats?.cards?.conversion_rate || 0}% CVR)`} icon={<BarChart3 size={16} style={{ color: "var(--color-gold)" }} />} />
          <StatCard label="Print Upsell Rate" value={`${stats?.print_upsell?.rate || 0}%`} sub={`${stats?.print_upsell?.print_orders || 0} of ${stats?.print_upsell?.total_paid || 0} orders`} icon={<Printer size={16} style={{ color: "var(--color-gold)" }} />} />
          <StatCard label="Signups" value={stats?.signups?.all_time || 0} sub={`${stats?.signups?.today || 0} today / ${stats?.signups?.week || 0} this week`} icon={<Users size={16} style={{ color: "var(--color-gold)" }} />} />
          <StatCard label="Emails" value={stats?.emails?.total || 0} sub={`${stats?.emails?.sent || 0} sent / ${stats?.emails?.failed || 0} failed`} icon={<Mail size={16} style={{ color: "var(--color-gold)" }} />} />
        </div>

        {/* Sport + Breed Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-5">
            <h3 className="font-heading text-lg uppercase tracking-tight text-[var(--color-text-dark)] mb-4">Sport Breakdown</h3>
            <div className="space-y-2.5">
              {sportsEntries.map(([s, c]) => <BarRow key={s} label={s} count={c} max={maxSport} />)}
              {sportsEntries.length === 0 && <p className="font-body text-xs text-[var(--color-text-light)]">No data yet</p>}
            </div>
          </div>
          <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-5">
            <h3 className="font-heading text-lg uppercase tracking-tight text-[var(--color-text-dark)] mb-4">Breed Breakdown</h3>
            <div className="space-y-2.5">
              {breedEntries.slice(0, 10).map(([b, c]) => <BarRow key={b} label={b} count={c} max={maxBreed} />)}
              {breedEntries.length === 0 && <p className="font-body text-xs text-[var(--color-text-light)]">No data yet</p>}
            </div>
          </div>
        </div>

        {/* Discount Codes */}
        <h2 className="font-heading text-2xl uppercase tracking-tight text-[var(--color-text-dark)] mb-4">Discount Codes</h2>
        <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-5 mb-4">
          <form onSubmit={createDiscount} className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 min-w-0">
              <label className="font-body text-xs text-[var(--color-text-light)] mb-1 block">Code</label>
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="e.g. LAUNCH20" className="rounded-[8px]" data-testid="discount-code-input" />
            </div>
            <div className="w-24">
              <label className="font-body text-xs text-[var(--color-text-light)] mb-1 block">% Off</label>
              <Input type="number" min={1} max={100} value={newPercent} onChange={(e) => setNewPercent(parseInt(e.target.value) || 0)} className="rounded-[8px]" data-testid="discount-percent-input" />
            </div>
            <div className="w-24">
              <label className="font-body text-xs text-[var(--color-text-light)] mb-1 block">Max Uses</label>
              <Input type="number" min={1} value={newMaxUses} onChange={(e) => setNewMaxUses(e.target.value)} placeholder="Unlimited" className="rounded-[8px]" data-testid="discount-max-uses-input" />
            </div>
            <button type="submit" className="font-heading tracking-widest uppercase text-sm px-5 py-2.5 rounded-[8px] shrink-0" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }} data-testid="discount-create-btn">
              Create
            </button>
          </form>
        </div>
        {discounts.length > 0 && (
          <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead><tr className="border-b border-[var(--color-border)]">
                  <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Code</th>
                  <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Discount</th>
                  <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Uses</th>
                  <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Status</th>
                  <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {discounts.map(dc => (
                    <tr key={dc.id} className="border-b border-[var(--color-border)] last:border-0">
                      <td className="font-accent text-sm text-[var(--color-text-dark)] px-4 py-3">{dc.code}</td>
                      <td className="font-body text-sm text-[var(--color-text-mid)] px-4 py-3">{dc.discount_percent}%</td>
                      <td className="font-body text-sm text-[var(--color-text-mid)] px-4 py-3">{dc.current_uses}{dc.max_uses ? `/${dc.max_uses}` : ''}</td>
                      <td className="px-4 py-3">
                        <span className={`font-accent text-xs px-2 py-0.5 rounded ${dc.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                          {dc.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleDiscount(dc.id)} className="font-body text-xs text-[var(--color-text-light)] hover:text-[var(--color-text-dark)]">
                          {dc.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl uppercase tracking-tight text-[var(--color-text-dark)]">
            <ShoppingCart size={18} className="inline mr-2" style={{ color: "var(--color-gold)" }} />Recent Orders
          </h2>
          <button
            onClick={() => {
              const rows = [["Order ID","Dog Name","Sport","Email","Amount","Discount Code","Print","Date"]];
              orders.forEach(o => rows.push([
                o.id, o.dog_name, o.sport, o.email,
                (o.amount_total / 100).toFixed(2),
                o.discount_code || "", o.includes_print ? "Yes" : "No",
                new Date(o.created_at).toLocaleDateString()
              ]));
              const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `cardmydog-orders-${new Date().toISOString().slice(0,10)}.csv`;
              a.click(); URL.revokeObjectURL(url);
            }}
            disabled={orders.length === 0}
            className="flex items-center gap-1.5 font-heading tracking-widest uppercase text-xs px-4 py-2 rounded-[8px] disabled:opacity-40"
            style={{ backgroundColor: "var(--color-navy)", color: "white" }}
            data-testid="export-csv-btn"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
        <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead><tr className="border-b border-[var(--color-border)]">
                <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Dog</th>
                <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Sport</th>
                <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Email</th>
                <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Amount</th>
                <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Print</th>
                <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Discount</th>
                <th className="font-accent text-[10px] tracking-widest uppercase text-[var(--color-text-light)] px-4 py-3">Date</th>
              </tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="font-body text-sm text-[var(--color-text-dark)] px-4 py-3">{o.dog_name}</td>
                    <td className="font-body text-sm text-[var(--color-text-mid)] px-4 py-3 capitalize">{o.sport}</td>
                    <td className="font-body text-xs text-[var(--color-text-mid)] px-4 py-3">{o.email}</td>
                    <td className="font-accent text-sm text-[var(--color-text-dark)] px-4 py-3">{fmt(o.amount_total)}</td>
                    <td className="px-4 py-3">{o.includes_print ? <span className="text-green-600 font-accent text-xs">Yes</span> : <span className="text-[var(--color-text-light)] font-accent text-xs">No</span>}</td>
                    <td className="font-body text-xs text-[var(--color-text-mid)] px-4 py-3">{o.discount_code ? `${o.discount_code} (${o.discount_percent}%)` : '—'}</td>
                    <td className="font-body text-xs text-[var(--color-text-light)] px-4 py-3">{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={7} className="text-center font-body text-sm text-[var(--color-text-light)] px-4 py-8">No orders yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
